import { type SelectOption } from '@aus-platform/design-system';
import { isEmpty, isNil, sumBy } from 'lodash';
import { v4 as uuid } from 'uuid';
import {
  type AddHeapClusterDashboardPayload,
  DashboardCategoryEnum,
  type DashboardDataCategoryEntry,
  type DashboardLeadDistancePayload,
  DashboardVolumeType,
  type FileDataType,
  type HeapClusterLeadDistance,
  type HeapClusterResponseDataTypeObj,
} from 'shared/api';
import { type DashboardCategoryConfig } from '../heap-list';
import {
  DEFAULT_DASHBOARD_VOLUME_TYPE,
  DEFAULT_VOLUME_TYPE_OPTION,
  VOLUME_TYPE_OPTIONS,
} from './constants';
import {
  type Attachment,
  type ClusterCumulativeMeasurements,
  type ClusterVolumeAndWeight,
  type DashboardConfigurationType,
  type LeadDistanceEntry,
  type LeadDistanceField,
} from './types';

// Fields that must be filled for a lead-distance card to be valid.
export const LEAD_DISTANCE_REQUIRED_FIELDS: LeadDistanceField[] = [
  'category3',
  'category4',
  'dumpVolume',
  'bulkingFactor',
  'pitDumpLeadDistanceKm',
  'intraPitLeadDistanceKm',
  'intraDumpLeadDistanceKm',
];

export const REQUIRED_FIELD_MESSAGE = 'This field is required.';

export const createEmptyLeadDistanceEntry = (): LeadDistanceEntry => ({
  clientId: uuid(),
  category3: null,
  category4: null,
  dumpVolume: null,
  bulkingFactor: null,
  pitDumpLeadDistanceKm: null,
  intraPitLeadDistanceKm: null,
  intraDumpLeadDistanceKm: null,
});

/**
 * In-situ (bank) volume is derived from the loose dump volume and the bulking
 * factor: inSitu = dumpVolume / bulkingFactor.
 */
export const computeInSituVolume = (
  dumpVolume: number | null,
  bulkingFactor: number | null,
): number | null => {
  if (
    isNil(dumpVolume) ||
    isNil(bulkingFactor) ||
    Number(bulkingFactor) === 0
  ) {
    return null;
  }

  return Number((Number(dumpVolume) / Number(bulkingFactor)).toFixed(2));
};

export const computeTotalLeadDistance = (entry: LeadDistanceEntry): number =>
  Number(
    sumBy(
      [
        entry.pitDumpLeadDistanceKm,
        entry.intraPitLeadDistanceKm,
        entry.intraDumpLeadDistanceKm,
      ],
      (value) => Number(value ?? 0),
    ).toFixed(2),
  );

export const isLeadDistanceEntryValid = (entry: LeadDistanceEntry): boolean =>
  LEAD_DISTANCE_REQUIRED_FIELDS.every((field) => !isNil(entry[field]));

type BuildDashboardPayloadParams = {
  dashboardFormValues: DashboardConfigurationType;
  selectedDashboardClusterId: string;
  isLeadDistanceEnabled: boolean;
  attachments: Attachment[];
};

type GetCategoryOptionParams = {
  categoryKey: DashboardCategoryEnum;
  entryId: string | null;
  dashboardCategoryConfig: DashboardCategoryConfig[];
};

const hasRequiredDashboardFields = (
  values: DashboardConfigurationType,
  isLeadDistanceEnabled: boolean,
): boolean => {
  const { accountingMonth, category1, category2, leadDistances } = values;

  if (isNil(accountingMonth) || isNil(category1) || isNil(category2)) {
    return false;
  }

  if (
    isLeadDistanceEnabled &&
    (isEmpty(leadDistances) || !leadDistances.every(isLeadDistanceEntryValid))
  ) {
    return false;
  }

  return true;
};

const buildLeadDistancesPayload = (
  leadDistances: LeadDistanceEntry[],
): DashboardLeadDistancePayload[] =>
  leadDistances.map((entry) => ({
    ...(entry.id ? { id: entry.id } : {}),
    dashboardDataCategory3Entry: entry.category3?.value?.id,
    dashboardDataCategory4Entry: entry.category4?.value?.id,
    dumpVolume: entry.dumpVolume,
    bulkingFactor: entry.bulkingFactor,
    pitDumpLeadDistanceKm: entry.pitDumpLeadDistanceKm,
    intraPitLeadDistanceKm: entry.intraPitLeadDistanceKm,
    intraDumpLeadDistanceKm: entry.intraDumpLeadDistanceKm,
  }));

const buildAttachmentPayload = (
  attachments: Attachment[],
): Partial<AddHeapClusterDashboardPayload> => ({
  fileIds: attachments
    .map(({ fileUploadId }) => fileUploadId)
    .filter((fileUploadId): fileUploadId is string => Boolean(fileUploadId)),
});

export const buildDashboardPayload = ({
  dashboardFormValues,
  selectedDashboardClusterId,
  isLeadDistanceEnabled,
  attachments,
}: BuildDashboardPayloadParams): AddHeapClusterDashboardPayload | null => {
  if (!hasRequiredDashboardFields(dashboardFormValues, isLeadDistanceEnabled)) {
    return null;
  }

  const {
    accountingMonth,
    category1,
    category2,
    volumeType,
    deletedLeadDistanceIds,
  } = dashboardFormValues;

  return {
    id: selectedDashboardClusterId,
    accountingMonth: accountingMonth!.getMonth() + 1,
    accountingYear: accountingMonth!.getFullYear(),
    dashboardDataCategory1Entry: category1!.value.id,
    dashboardDataCategory2Entry: category2!.value.id,
    dashboardVolumeType: volumeType?.value ?? DEFAULT_DASHBOARD_VOLUME_TYPE,
    includeLeadDistanceInfo: isLeadDistanceEnabled,

    ...(isLeadDistanceEnabled
      ? {
          leadDistances: buildLeadDistancesPayload(
            dashboardFormValues.leadDistances,
          ),
        }
      : {}),

    ...(deletedLeadDistanceIds.length > 0 ? { deletedLeadDistanceIds } : {}),

    ...buildAttachmentPayload(attachments),
  };
};

const getCategoryOption = ({
  categoryKey,
  entryId,
  dashboardCategoryConfig,
}: GetCategoryOptionParams) => {
  if (isNil(entryId)) {
    return null;
  }

  const category = dashboardCategoryConfig.find(
    ({ categoryKey: key }) => key === categoryKey,
  );

  if (isNil(category)) {
    return null;
  }

  return (
    category.options.find(
      (option: SelectOption<DashboardDataCategoryEntry>) =>
        option.value.id === entryId,
    ) ?? null
  );
};

// Falls back to the default (net) option for missing or unknown volume types.
export const getVolumeTypeOption = (
  volumeType?: DashboardVolumeType | null,
): SelectOption<DashboardVolumeType> =>
  VOLUME_TYPE_OPTIONS.find(({ value }) => value === volumeType) ??
  DEFAULT_VOLUME_TYPE_OPTION;

/**
 * Picks the cluster's cumulative volume and weight matching the volume type
 * selected for the dashboard (cut, fill or net).
 */
export const getClusterVolumeAndWeightByType = (
  cluster: ClusterCumulativeMeasurements,
  volumeType: DashboardVolumeType,
): ClusterVolumeAndWeight => {
  const volumeAndWeightByType: Record<
    DashboardVolumeType,
    ClusterVolumeAndWeight
  > = {
    [DashboardVolumeType.Cut]: {
      volume: cluster.cumulativeCutVolume,
      weight: cluster.cumulativeCutWeight,
    },
    [DashboardVolumeType.Fill]: {
      volume: cluster.cumulativeFillVolume,
      weight: cluster.cumulativeFillWeight,
    },
    [DashboardVolumeType.Net]: {
      volume: cluster.cumulativeNetVolume,
      weight: cluster.cumulativeNetWeight,
    },
  };

  return volumeAndWeightByType[volumeType];
};

export const mapExistingAttachments = (
  attachments: FileDataType[],
): Attachment[] =>
  attachments.map((attachment: FileDataType) => {
    const file = new File(
      [new Blob([], { type: attachment.extension })],
      attachment.filename,
      {
        type: attachment.extension,
      },
    );

    Object.defineProperty(file, 'size', {
      value: Number(attachment.size) || 0,
    });

    return {
      id: attachment.id,
      file,
      fileUploadId: attachment.id,
      isExisting: true,
    };
  });

const buildLeadDistanceEntryFromResponse = (
  leadDistance: HeapClusterLeadDistance,
  dashboardCategoryConfig: DashboardCategoryConfig[],
): LeadDistanceEntry => ({
  id: leadDistance.id,
  clientId: uuid(),
  category3: getCategoryOption({
    categoryKey: DashboardCategoryEnum.Category3,
    entryId: leadDistance.dashboardDataCategory3Entry,
    dashboardCategoryConfig,
  }),
  category4: getCategoryOption({
    categoryKey: DashboardCategoryEnum.Category4,
    entryId: leadDistance.dashboardDataCategory4Entry,
    dashboardCategoryConfig,
  }),
  dumpVolume: leadDistance.dumpVolume,
  bulkingFactor: leadDistance.bulkingFactor,
  pitDumpLeadDistanceKm: leadDistance.pitDumpLeadDistanceKm,
  intraPitLeadDistanceKm: leadDistance.intraPitLeadDistanceKm,
  intraDumpLeadDistanceKm: leadDistance.intraDumpLeadDistanceKm,
});

const getDefaultLeadDistances = (
  heapClusterData: HeapClusterResponseDataTypeObj,
  dashboardCategoryConfig: DashboardCategoryConfig[],
): LeadDistanceEntry[] => {
  const { leadDistances } = heapClusterData;

  if (isEmpty(leadDistances)) {
    return [createEmptyLeadDistanceEntry()];
  }

  return leadDistances!.map((leadDistance) =>
    buildLeadDistanceEntryFromResponse(leadDistance, dashboardCategoryConfig),
  );
};

export const getDashboardDefaultValues = (
  heapClusterData: HeapClusterResponseDataTypeObj,
  dashboardCategoryConfig: DashboardCategoryConfig[],
  accountingMonth: Date | null = null,
): DashboardConfigurationType => {
  const {
    dashboardDataCategory1Entry,
    dashboardDataCategory2Entry,
    dashboardVolumeType,
    includeLeadDistanceInfo,
  } = heapClusterData;

  return {
    category1: getCategoryOption({
      categoryKey: DashboardCategoryEnum.Category1,
      entryId: dashboardDataCategory1Entry,
      dashboardCategoryConfig,
    }),

    category2: getCategoryOption({
      categoryKey: DashboardCategoryEnum.Category2,
      entryId: dashboardDataCategory2Entry,
      dashboardCategoryConfig,
    }),

    accountingMonth,
    volumeType: getVolumeTypeOption(dashboardVolumeType),
    includeLeadDistanceInfo,
    deletedLeadDistanceIds: [],
    leadDistances: getDefaultLeadDistances(
      heapClusterData,
      dashboardCategoryConfig,
    ),
  };
};
